import React, { useState, useEffect } from 'react';
import { getTherapyMaterialByID } from '../../../../controllers/TherapyMaterialController';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const DetailModal = ({ t }) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [therapyMaterial, setTherapyMaterial] = useState([]);
    const navigate = useNavigate();
    
    const transform = (slot) => ({
        ...slot,
        Open: () => null,
        OpenMenuItem: () => null,
        SwitchTheme: () => null,
        SwitchThemeMenuItem: () => null
    });
    
    const defaultLayout = defaultLayoutPlugin({
        renderToolbar: (Toolbar) => {
            const { renderDefaultToolbar } = defaultLayoutPlugin().toolbarPluginInstance;
            return (
                <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
            );
        },
        sidebarTabs: () => [],
    });
    
    useEffect(() => {
        const fetchTherapyMaterial = async () => {
            try {
                const result = await getTherapyMaterialByID(id);
                if (!result) {
                    throw new Error(t('therapyMaterial.fetchData.errorMessage'));
                };
                
                setTherapyMaterial(result);
            } catch (error) {
                toast.error(error.message);
                navigate('/therapy-material');
            } finally {
                setLoading(false);
            };
        };
        
        fetchTherapyMaterial();
    }, [id, t, navigate]);
    
    if (loading) {
        return (
            <div className='space-y-5'>
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
            </div>
        ); 
    };
    
    const handleEdit = () => {
        navigate(`/therapy-material/${id}/edit`, { state: { fromDetail: true } });
    };
    
    const handleBack = () => {
        navigate('/therapy-material');
    };
    
    return (
        <div className='space-y-5'>
            {/* Thumbnail */}
            {therapyMaterial.image_url && (
                <div className='flex items-center justify-center rounded-lg'>
                    <img
                        src={therapyMaterial.image_url}
                        alt='Thumbnail'
                        className='h-36 md:h-64 rounded shadow-md object-cover'
                    />
                </div>
            )}
            
            {/* Main Content */}
            <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
                {/* Title */}
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.title.title')} ({t('languages.indonesian')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={therapyMaterial.title_id}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.title.title')} ({t('languages.english')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={therapyMaterial.title_en}
                    />
                </div>
                
                {/* Description */}
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.description.title')} ({t('languages.indonesian')}):</span>
                    <textarea
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={therapyMaterial.description_id}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.description.title')} ({t('languages.english')}):</span>
                    <textarea
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={therapyMaterial.description_en}
                    />
                </div>
                
                {/* Type */}
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.type.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={t(`common.type.${therapyMaterial.type.toLowerCase()}`)}
                    />
                </div>
                
                {/* Video Url */}
                {therapyMaterial.type === 'Video' && therapyMaterial.video_url && (
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.videoLink.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={therapyMaterial.video_url}
                        />
                    </div>
                )}
                
                {/* Duration */}
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.duration.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        disabled
                        value={therapyMaterial.duration}
                    />
                </div>
                <div className='flex justify-center sm:justify-end space-x-5'>
                    <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                    <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' onClick={handleEdit}>{t('common.action.edit')}</ButtonPrimary>
                </div>
            </div>
            
            {/* Article Viewer */}
            {therapyMaterial.type === 'Article' && therapyMaterial.article_url && (
                <div className='flex p-5 rounded-lg bg-white'>
                    <div className='w-full p-3 border border-platinum rounded'>
                        <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js'>
                            <div className='h-[320px] md:h-[640px]'>
                                <Viewer
                                    fileUrl={therapyMaterial.article_url}
                                    plugins={[defaultLayout]}
                                    defaultScale={SpecialZoomLevel.PageWidth}
                                />
                            </div>
                        </Worker>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetailModal;
